import { Button } from '@bluevalet/common-ui';
import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import CookieConsentModal from './CookieConsentModal';
import GooglePrivacyLink from './GooglePrivacyLink';
import { ConsentChoice, ConsentState, COOKIE_CONSENT_OPEN_EVENT, generateCookieContentState } from './service';

const CONTAINER_ID = 'cookie-consent';

// Les deux boutons de la bannière : seuls la mesure d'audience et la publicité sont proposées.
const ACCEPTED_CHOICE: ConsentChoice = {
  adStorage: true,
  adUserData: true,
  adPersonalization: true,
  analyticsStorage: true,
  functionalityStorage: false,
  personalizationStorage: false,
  securityStorage: false,
};

const REFUSED_CHOICE: ConsentChoice = {
  adStorage: false,
  adUserData: false,
  adPersonalization: false,
  analyticsStorage: false,
  functionalityStorage: false,
  personalizationStorage: false,
  securityStorage: false,
};

interface CookieConsentProps {
  display: boolean;
  consent: ConsentState | null;
  setConsent: (consent: ConsentState) => void;
  url: string | null;
}

const CookieConsent: FC<CookieConsentProps> = ({ display, consent, setConsent, url }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const containerClassName = classNames('CookieConsentContainer', {
    CookieConsentDisplay: display,
  });

  useEffect(() => {
    const handler = () => setIsModalOpen(true);
    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, handler);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, handler);
    };
  }, []);

  // La bannière vit dans un portail créé à la volée, hors de l'îlot monté par le serveur.
  useEffect(() => {
    if (!container) {
      const created = document.createElement('div');
      created.id = CONTAINER_ID;
      setContainer(created);

      document.body.appendChild(created);
    }
  }, [container]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConsentValidateChoice = (choice: ConsentChoice) => {
    setConsent(generateCookieContentState(choice));
  };

  const handleConsentValidateAllChoice = () => {
    setConsent(generateCookieContentState(ACCEPTED_CHOICE));
  };

  const handleConsentValidateNoChoice = () => {
    setConsent(generateCookieContentState(REFUSED_CHOICE));
  };

  return (
    <>
      {container !== null &&
        ReactDOM.createPortal(
          <>
            <div className={containerClassName}>
              <div>
                <div className="CookieConsentTitle">
                  <span>{t('cookies-consent-popInTitle')}</span>
                </div>
                <p>{t('cookies-consent-popInText')}</p>
                <GooglePrivacyLink />
              </div>
              <div className="CookieConsentButtons">
                <button type="button" className="personalizedCookieButton" onClick={handleOpenModal}>
                  <span>{t('cookies-consent-personalizeLabel')}</span>
                </button>
                <div className="CookieConsentButtonsResponse">
                  <Button label={t('cookies-consent-refuseLabel')} onClick={handleConsentValidateNoChoice} small />
                  <Button
                    label={t('cookies-consent-acceptLabel')}
                    onClick={handleConsentValidateAllChoice}
                    primary
                    small
                  />
                </div>
              </div>
            </div>
            <CookieConsentModal
              isOpen={isModalOpen}
              validateChoice={handleConsentValidateChoice}
              onRequestClose={handleCloseModal}
              consent={consent}
              url={url}
            />
          </>,
          container,
        )}
    </>
  );
};

export default CookieConsent;
